import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  List,
  Divider,
  Typography,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  useMediaQuery,
  useTheme,
  Paper,
  Stack,
} from '@mui/material'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import PersonIcon from '@mui/icons-material/Person'
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined'
import LogoutIcon from '@mui/icons-material/Logout'
import LockResetIcon from '@mui/icons-material/LockReset'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { CommonButton, CommonDialog } from '@/components/common'
import { ChairpersonResetPasswordForm } from '@/components/auth'
import { useChairpersonLogout } from '@/hooks'
import { useChairpersonContext } from '@/context'
import { forcyteService } from '@/services'
import { ROUTES } from '@/constants'

const wrapLongText = {
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
}

const RecruiterCard = ({ recruiter, accountNames = [], isSelectedAccount }) => (
  <Paper
    elevation={0}
    sx={{
      py: { xs: 1, md: 1.25 },
      px: { xs: 1.25, md: 1.5 },
      borderRadius: { xs: '6px', md: '8px' },
      bgcolor: isSelectedAccount ? 'secondary.main' : 'grey.100',
      border: '1px solid',
      borderColor: isSelectedAccount ? 'primary.main' : 'divider',
      minWidth: 0,
      maxWidth: '100%',
    }}
  >
    <Stack
      direction='row'
      spacing={1.25}
      alignItems='flex-start'
      sx={{ minWidth: 0 }}
    >
      <SupportAgentOutlinedIcon
        sx={{ fontSize: 22, color: 'primary.main', mt: 0.25, flexShrink: 0 }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {recruiter.name ? (
          <Typography
            variant='body2'
            sx={{ fontWeight: 600, lineHeight: 1.4, ...wrapLongText }}
          >
            {recruiter.name}
          </Typography>
        ) : null}
        {accountNames.length > 0 ? (
          <Typography
            variant='caption'
            sx={{
              display: 'block',
              color: 'text.secondary',
              fontSize: '0.7rem',
              mb: 0.25,
              ...wrapLongText,
            }}
          >
            {accountNames.join(', ')}
          </Typography>
        ) : null}
        {recruiter.email ? (
          <Typography
            variant='body2'
            sx={{
              color: 'text.secondary',
              fontSize: '0.8rem',
              ...wrapLongText,
            }}
          >
            {recruiter.email}
          </Typography>
        ) : null}
        {recruiter.phone ? (
          <Typography
            variant='body2'
            sx={{
              color: 'text.secondary',
              fontSize: '0.8rem',
              ...wrapLongText,
            }}
          >
            {recruiter.phone}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  </Paper>
)

export const ChairpersonSidebar = ({ selectedAccountId, onAccountChange }) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [expanded, setExpanded] = useState(!isMobile)
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false)
  const { logout, loadingLogout } = useChairpersonLogout()
  const { chairperson, setChairperson } = useChairpersonContext()

  const recruiters = useMemo(() => {
    const byId = new Map()

    for (const account of chairperson?.accounts ?? []) {
      const recruiter = account.recruiter
      if (!recruiter?.id) continue

      const key = String(recruiter.id)
      const existing = byId.get(key)

      if (existing) {
        existing.accountNames.push(account.name)
        existing.accountIds.push(account.id)
      } else {
        byId.set(key, {
          recruiter,
          accountNames: [account.name],
          accountIds: [account.id],
        })
      }
    }

    return Array.from(byId.values())
  }, [chairperson?.accounts])

  const showAccountNames =
    recruiters.length > 1 ||
    recruiters.some(entry => entry.accountNames.length > 1)

  const handleLogout = async () => {
    try {
      await logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      forcyteService.clearToken()
      setChairperson({})
      navigate(ROUTES.CHAIRPERSON_LOGIN)
    }
  }

  return (
    <Box
      data-testid='chairperson-sidebar'
      sx={{
        width: '100%',
        minWidth: 0,
      }}
    >
      {/* Title */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography
          variant='h5'
          sx={{
            color: 'primary.main',
            fontWeight: 700,
            typography: { xs: 'h6', md: 'h5' },
          }}
        >
          Chairperson Portal
        </Typography>
        <CommonButton
          iconOnly={<KeyboardArrowDownIcon fontSize='small' />}
          variant='text'
          size='small'
          aria-label='toggle sidebar'
          aria-expanded={expanded}
          onClick={() => setExpanded(prev => !prev)}
          sx={{
            display: { xs: 'inline-flex', md: 'none' },
            ml: 0.5,
            transition: 'transform 150ms ease',
            transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
          }}
        />
      </Box>

      <Collapse in={expanded} timeout='auto'>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
          }}
        >
          {/* Chairperson Info Card */}
          <Paper
            elevation={0}
            sx={{
              py: { xs: 1, md: 1.25 },
              px: { xs: 1, md: 2 },
              mt: { xs: 0.5, md: 1 },
              mb: { xs: 1, md: 2 },
              mx: 1,
              bgcolor: 'primary.main',
              borderRadius: { xs: '6px', md: '8px' },
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <PersonIcon sx={{ color: 'white', fontSize: 28, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant='subtitle1'
                sx={{
                  fontWeight: 600,
                  lineHeight: 1.3,
                  color: 'white',
                  ...wrapLongText,
                }}
              >
                {chairperson?.first_name} {chairperson?.last_name}
              </Typography>
              {chairperson?.title && (
                <Typography
                  variant='body2'
                  sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' }}
                >
                  {chairperson.title}
                </Typography>
              )}
            </Box>
          </Paper>

          {/* Accounts Section */}
          <Typography
            variant='subtitle2'
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              mb: 1,
              mt: 1,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            Accounts
          </Typography>

          <List disablePadding>
            {chairperson?.accounts?.map(account => (
              <ListItemButton
                key={account.id}
                selected={account.id === selectedAccountId}
                onClick={() => onAccountChange?.(account.id)}
                sx={theme => ({
                  mt: { xs: 0.5, md: 1 },
                  mb: { xs: 0.5, md: 1 },
                  borderRadius: { xs: '6px', md: '8px' },
                  py: { xs: 1, md: 1.25 },
                  px: { xs: 1, md: 2 },
                  '&.Mui-selected': {
                    color: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.secondary,
                  },
                })}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <AccountCircleIcon
                    color={
                      account.id === selectedAccountId ? 'primary' : 'action'
                    }
                  />
                </ListItemIcon>
                <ListItemText
                  primary={account.name}
                  secondary={account.role?.name}
                  primaryTypographyProps={{
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    sx: wrapLongText,
                  }}
                  secondaryTypographyProps={{
                    fontSize: '0.75rem',
                    sx: wrapLongText,
                  }}
                />
              </ListItemButton>
            ))}
          </List>

          {recruiters.length > 0 ? (
            <Box sx={{ mt: 2, mx: 1, minWidth: 0 }}>
              <Typography
                variant='subtitle2'
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600,
                  mb: 1,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                {recruiters.length > 1
                  ? 'Account Representatives'
                  : 'Account Representative'}
              </Typography>
              <Stack spacing={1}>
                {recruiters.map(({ recruiter, accountNames, accountIds }) => (
                  <RecruiterCard
                    key={recruiter.id}
                    recruiter={recruiter}
                    accountNames={showAccountNames ? accountNames : []}
                    isSelectedAccount={accountIds.includes(selectedAccountId)}
                  />
                ))}
              </Stack>
            </Box>
          ) : null}

          {/* Reset password */}
          <ListItemButton
            onClick={() => setResetPasswordOpen(true)}
            sx={{
              mt: { xs: 0.5, md: 1 },
              mb: { xs: 0.5, md: 1 },
              mx: 1,
              borderRadius: { xs: '6px', md: '8px' },
              py: { xs: 1, md: 1.25 },
              px: { xs: 1, md: 2 },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <LockResetIcon color='action' />
            </ListItemIcon>
            <ListItemText
              primary='Reset password'
              primaryTypographyProps={{
                fontWeight: 600,
                fontSize: '0.9rem',
              }}
            />
          </ListItemButton>

          {/* Logout Button */}
          <Box sx={{ mt: 'auto' }}>
            <Divider sx={{ mb: { xs: 1, md: 2 } }} />
            <ListItemButton
              onClick={handleLogout}
              disabled={loadingLogout}
              sx={{
                mt: { xs: 0.5, md: 1 },
                mb: { xs: 0.5, md: 1 },
                mx: 1,
                borderRadius: { xs: '6px', md: '8px' },
                py: { xs: 1, md: 1.25 },
                px: { xs: 1, md: 2 },
                color: 'error.main',
                '&:hover': {
                  backgroundColor: 'error.lighter',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <LogoutIcon color='error' />
              </ListItemIcon>
              <ListItemText
                primary={loadingLogout ? 'Logging out...' : 'Logout'}
                primaryTypographyProps={{
                  fontWeight: 600,
                  fontSize: '0.9rem',
                }}
              />
            </ListItemButton>
          </Box>
        </Box>
      </Collapse>

      <CommonDialog
        open={resetPasswordOpen}
        onClose={() => setResetPasswordOpen(false)}
        title='Reset password'
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <Box sx={{ py: 1, px: { xs: 0, sm: 1 } }}>
          <ChairpersonResetPasswordForm
            variant='dialog'
            showRequiredMessage={false}
            onSuccess={() => setResetPasswordOpen(false)}
          />
        </Box>
      </CommonDialog>
    </Box>
  )
}
